/**
 * HmWarehouseDetail — formatka dokumentu magazynowego (PODGLĄD).
 *
 * Pozycje magazynowe mają TYLKO cenę i wartość netto — Symfonia nie podaje tu brutto ani VAT
 * (sprawdzone na żywej instancji). Kolumn, których nie ma, nie udajemy zerami: tabela pozycji
 * jest po prostu węższa niż na sprzedaży.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { HmWarehouseDetail as HmWarehouseDetailDto } from '@saas/shared';
import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/Button';
import { DeliveriesButton } from '@/components/handel-magazyn/DeliveriesButton';
import {
  day,
  DetailError,
  DetailHeader,
  DetailTabBody,
  DetailTabs,
  Field,
  PdfPreviewModal,
  Section,
  TotalsBar,
  useDocumentTabLabel,
  usePdfPreview,
} from '@/components/handel-magazyn/detail-parts';
import { HmApprovalSection } from '@/components/handel-magazyn/HmApprovalSection';
import {
  PrintDocumentModal,
  printSettingsToParams,
} from '@/components/handel-magazyn/PrintDocumentModal';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

const TABS = ['positions', 'extra'] as const;
type Tab = (typeof TABS)[number];

export function HmWarehouseDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = useActiveCompanyScope().companyId;
  const [tab, setTab] = useState<Tab>('positions');
  const pdf = usePdfPreview(`/handel-magazyn/warehouse-documents/${id}/pdf?companyId=${companyId}`);
  const [printOpen, setPrintOpen] = useState(false);

  const query = useQuery({
    queryKey: ['handel-magazyn', 'warehouse-document', id, companyId],
    enabled: !!id && !!companyId,
    retry: false,
    queryFn: async (): Promise<HmWarehouseDetailDto> => {
      const res = await api.get<HmWarehouseDetailDto>(
        `/handel-magazyn/warehouse-documents/${id}?companyId=${companyId}`,
      );
      return res.data;
    },
  });

  // Numer dokumentu znamy dopiero po odpowiedzi — podmieniamy etykiete karty
  // z ogolnej na konkretny numer.
  useDocumentTabLabel(`/handel-magazyn/magazyn/${id}`, query.data?.documentNumber);

  if (query.isLoading) {
    return <div className="p-4 text-sm text-text-secondary">{t('common.loading')}</div>;
  }
  if (query.error) return <DetailError error={query.error} />;

  const doc = query.data!;

  return (
    <div className="flex h-full flex-col p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <DetailHeader
            documentNumber={doc.documentNumber}
            onPrint={() => setPrintOpen(true)}
            printLoading={pdf.loading}
          />
        </div>
        {/* Edycja pokazywana TYLKO dla dokumentu w buforze. To nasze ZAŁOŻENIE ostrożnościowe,
            nie zweryfikowany fakt: dokument zatwierdzony zmienił już stany magazynowe, więc
            edycja albo zostanie odrzucona, albo zmieni stany „po cichu" — jedno i drugie jest
            złym domyślnym zachowaniem. Do sprawdzenia na żywej instancji; jeśli Symfonia na to
            pozwala i jest to pożądane, warunek wystarczy zdjąć. */}
        {doc.inBuffer && (
          <div className="mt-1 shrink-0">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Pencil className="h-4 w-4" />}
              onClick={() => navigate(`/handel-magazyn/magazyn/${doc.id}/edycja`)}
              data-testid="hm-warehouse-edit"
            >
              {t('common.edit')}
            </Button>
          </div>
        )}
      </div>

      {/* Obieg akceptacji — patrz `HmApprovalSection`: dokument żyje w Symfonii, u nas powstaje
          wyłącznie rezerwacja identyfikatora, i to dopiero przy wysłaniu do akceptacji. */}
      <div className="mb-3">
        <HmApprovalSection kind="warehouse" externalId={doc.id} companyId={companyId} />
      </div>

      <div className="grid grid-cols-1 gap-6 rounded-md border border-border-primary bg-bg-secondary p-4 lg:grid-cols-2">
        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.detail.documentType')}>
            <Field label={t('tenant.handelMagazyn.columns.typeCode')} value={doc.typeCode} />
            <Field label={t('tenant.handelMagazyn.detail.series')} value={doc.series} />
            <Field
              label={t('tenant.handelMagazyn.columns.status')}
              value={
                doc.inBuffer
                  ? t('tenant.handelMagazyn.status.buffer')
                  : t('tenant.handelMagazyn.status.issued')
              }
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.columns.contractor')}>
            <Field label={t('tenant.handelMagazyn.detail.name')} value={doc.contractorName} />
          </Section>
        </div>

        <div className="space-y-4">
          <Section title={t('tenant.handelMagazyn.warehouse.columns.warehouse')}>
            <Field
              label={t('tenant.handelMagazyn.warehouse.detail.source')}
              value={doc.warehouseName}
            />
            {/* Magazyn docelowy ma sens tylko dla przesunięć — na WZ/PZ zostanie kreska. */}
            <Field
              label={t('tenant.handelMagazyn.warehouse.detail.target')}
              value={doc.receivingWarehouseName}
            />
          </Section>

          <Section title={t('tenant.handelMagazyn.detail.dates')}>
            <Field label={t('tenant.handelMagazyn.columns.issueDate')} value={day(doc.issueDate)} />
            <Field
              label={t('tenant.handelMagazyn.warehouse.columns.operationDate')}
              value={day(doc.operationDate)}
            />
          </Section>
        </div>
      </div>

      <DetailTabs tabs={TABS} active={tab} onChange={setTab} />

      <DetailTabBody>
        {tab === 'positions' && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-primary text-left text-xs uppercase tracking-wide text-text-tertiary">
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.no')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.code')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.description')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.detail.pos.quantity')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.unit')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.detail.pos.price')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.columns.net')}
                </th>
              </tr>
            </thead>
            <tbody>
              {doc.positions.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-2 py-6 text-center text-text-tertiary">
                    {t('tenant.handelMagazyn.detail.noPositions')}
                  </td>
                </tr>
              )}
              {doc.positions.map((p) => (
                <tr key={p.id} className="border-b border-border-primary/60">
                  <td className="px-2 py-1.5 text-text-tertiary">{p.no}</td>
                  <td className="px-2 py-1.5 font-mono text-xs text-text-primary">
                    {p.productCode}
                  </td>
                  <td className="px-2 py-1.5 text-text-primary">{p.description}</td>
                  <td className="px-2 py-1.5 text-right text-text-primary">
                    <span className="inline-flex items-center justify-end gap-1">
                      {p.quantity}
                      <DeliveriesButton position={p} inBuffer={doc.inBuffer} />
                    </span>
                  </td>
                  <td className="px-2 py-1.5 text-text-secondary">{p.unit}</td>
                  <td className="px-2 py-1.5 text-right text-text-secondary">{p.priceValue}</td>
                  <td className="px-2 py-1.5 text-right font-medium text-text-primary">
                    {p.netValue}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 'extra' && (
          <div className="space-y-2">
            <Field label={t('tenant.handelMagazyn.detail.description')} value={doc.description} />
            <Field label={t('tenant.handelMagazyn.detail.note')} value={doc.note} />
          </div>
        )}
      </DetailTabBody>

      <TotalsBar
        items={[
          { label: t('tenant.handelMagazyn.columns.net'), value: doc.netValue },
          {
            label: t('tenant.handelMagazyn.warehouse.columns.documentValue'),
            value: `${doc.documentValue} ${doc.currency}`,
            strong: true,
          },
        ]}
      />

      <PdfPreviewModal url={pdf.url} title={doc.documentNumber} onClose={pdf.close} />
      <PrintDocumentModal
        open={printOpen}
        kind="warehouse"
        companyId={companyId}
        documentNumber={doc.documentNumber}
        onClose={() => setPrintOpen(false)}
        onPrint={(settings) => {
          setPrintOpen(false);
          void pdf.show(printSettingsToParams(settings));
        }}
      />
    </div>
  );
}

export default HmWarehouseDetail;
